import type {
  AuthSession,
  BootstrapCatalog,
  CorrectionDeliveryState,
  CorrectionPayload,
  CorrectionSubmitResult,
  HealthStatus,
  SubmitPayload,
  SubmitResult,
  TodaySalesResponse,
  VisitDeliveryState
} from './types';

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code: string
  ) {
    super(message);
  }
}

async function requestJson<T>(url: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(url, {
      ...init,
      headers: {
        accept: 'application/json',
        ...(init?.body ? { 'content-type': 'application/json' } : {}),
        ...init?.headers
      }
    });
  } catch {
    throw new ApiError('目前無法連線，資料仍保留在這支手機。', 0, 'NETWORK_ERROR');
  }

  const body = await response.json().catch(() => null) as Record<string, unknown> | null;
  if (!response.ok) {
    const message = typeof body?.message === 'string'
      ? body.message
      : typeof body?.error === 'string'
        ? body.error
        : '系統暫時無法處理，請稍後再試。';
    const code = typeof body?.code === 'string' ? body.code : `HTTP_${response.status}`;
    throw new ApiError(message, response.status, code);
  }
  return body as T;
}

export function getBootstrap(fresh = false): Promise<BootstrapCatalog> {
  return requestJson<BootstrapCatalog>(fresh ? `/api/bootstrap?refresh=${Date.now()}` : '/api/bootstrap',
    fresh ? { cache: 'no-store' } : undefined);
}

export function getAuthSession(): Promise<AuthSession> {
  return requestJson<AuthSession>('/api/auth/session');
}

export function signInWithGoogle(credential: string): Promise<AuthSession> {
  return requestJson<AuthSession>('/api/auth/google', {
    method: 'POST',
    body: JSON.stringify({ credential })
  });
}

export function signOutGoogle(): Promise<{ ok: boolean }> {
  return requestJson<{ ok: boolean }>('/api/auth/logout', { method: 'POST' });
}

export function submitVisit(payload: SubmitPayload): Promise<SubmitResult> {
  return requestJson<SubmitResult>('/api/visits', {
    method: 'POST',
    body: JSON.stringify(payload)
  });
}

export function getVisitStatus(visitId: string): Promise<VisitDeliveryState> {
  return requestJson<VisitDeliveryState>(`/api/visits/${encodeURIComponent(visitId)}`);
}

export function getTodaySales(): Promise<TodaySalesResponse> {
  return requestJson<TodaySalesResponse>('/api/me/today-sales', { cache: 'no-store' });
}

export function submitCorrection(unitId: string, payload: CorrectionPayload): Promise<CorrectionSubmitResult> {
  return requestJson<CorrectionSubmitResult>(`/api/me/sales/${encodeURIComponent(unitId)}/corrections`, {
    method: 'POST',
    body: JSON.stringify(payload)
  });
}

export function getCorrectionStatus(correctionId: string): Promise<CorrectionDeliveryState> {
  return requestJson<CorrectionDeliveryState>(`/api/corrections/${encodeURIComponent(correctionId)}`, { cache: 'no-store' });
}

export function retryCorrection(correctionId: string): Promise<CorrectionDeliveryState> {
  return requestJson<CorrectionDeliveryState>(`/api/corrections/${encodeURIComponent(correctionId)}/retry`, {
    method: 'POST'
  });
}

export function getHealthStatus(): Promise<HealthStatus> {
  return requestJson<HealthStatus>('/healthz', { cache: 'no-store' });
}

export async function downloadMonthlyReport(month: string, managerPassword: string, reportType: 'sales' | 'merchandising' = 'sales'): Promise<{ blob: Blob; filename: string }> {
  let response: Response;
  try {
    response = await fetch(`/api/reports/${reportType === 'merchandising' ? 'merchandising' : 'monthly'}.xlsx?month=${encodeURIComponent(month)}`, {
      headers: { 'x-manager-password': managerPassword }
    });
  } catch {
    throw new ApiError('目前無法連線，請稍後再試。', 0, 'NETWORK_ERROR');
  }
  if (!response.ok) {
    const body = await response.json().catch(() => null) as Record<string, unknown> | null;
    throw new ApiError(
      typeof body?.message === 'string' ? body.message : '月報產生失敗，請稍後再試。',
      response.status,
      typeof body?.code === 'string' ? body.code : `HTTP_${response.status}`
    );
  }
  const disposition = response.headers.get('content-disposition') || '';
  const encodedName = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  return {
    blob: await response.blob(),
    filename: encodedName ? decodeURIComponent(encodedName) : `${month.replace('-', '')}-駐點銷售報表.xlsx`
  };
}
